import styled from '@emotion/styled';
import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { colors as dsColors } from '@modernhealth/vital-foundation';
import {
    DeprecatedButton,
    BodyTextMedium,
    HeadingText40,
    pxToRem,
} from '@modernhealth/vital-components';
import paperPlaneIllustration from 'src/public/ds/illustrations/paper-plane-illustration.svg';
import { Modal } from 'src/shared/components/Modal';

const StyledModal = styled(Modal)({
    '.ant-modal-content': {
        borderRadius: pxToRem(20),

        '.ant-modal-body': {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: pxToRem(32),
        },

        '.ant-modal-close': {
            top: pxToRem(4),
            right: pxToRem(4),
        },

        '[class*="Button"]': {
            width: '100%',
        },
    },
});

const ResendLink = styled.a({
    color: dsColors.blue55,
    fontWeight: 600,
    textDecoration: 'underline',
    '&:hover': {
        color: dsColors.blue30,
        textDecoration: 'underline',
    },
});

const PaperPlaneIllustration = styled('img')({
    width: pxToRem(140),
    height: pxToRem(130),
    marginTop: pxToRem(24),
});

export enum ChatInfoMobileResendLinkModalTestID {
    Wrapper = 'ChatInfoMobileResendLinkModal.Wrapper',
    CloseButton = 'ChatInfoMobileResendLinkModal.CloseButton',
}

interface ChatInfoMobileResendLinkModalProps {
    visible: boolean;
    onSendLinkClick: () => void;
    onClose: () => void;
}

export const ChatInfoMobileResendLinkModal: FC<ChatInfoMobileResendLinkModalProps> = ({
    visible,
    onSendLinkClick,
    onClose,
}) => {
    const { t } = useTranslation();

    return (
        <StyledModal
            visible={visible}
            width={558}
            centered
            footer={null}
            onCancel={onClose}
            closeButtonTestID={ChatInfoMobileResendLinkModalTestID.CloseButton}
            wrapProps={{
                'data-testid': ChatInfoMobileResendLinkModalTestID.Wrapper,
            }}
        >
            <PaperPlaneIllustration src={paperPlaneIllustration} alt="Paper plane" />
            <HeadingText40 marginTop={12} marginBottom={16} centered>
                {t('chatInfoModal.linkSent.title')}
            </HeadingText40>
            <BodyTextMedium fontColor="gray65" marginBottom={24} centered>
                {t('chatInfoModal.linkSent.description')}
            </BodyTextMedium>
            <DeprecatedButton
                title={t('chatInfoModal.linkSent.backToHome')}
                onClick={onClose}
                size="large"
                fullWidth
            />
            <BodyTextMedium fontColor="gray65" marginTop={20}>
                <Trans
                    t={t}
                    i18nKey="chatInfoModal.linkSent.resendLink"
                    components={{
                        resend: <ResendLink onClick={onSendLinkClick} />,
                    }}
                />
            </BodyTextMedium>
        </StyledModal>
    );
};
